import { useEffect, useState, useCallback } from 'react';
import { AdminSidebar } from '../../components/Sidebar';
import { SkeletonCard } from '../../components/Skeleton';
import api from '../../utils/api';

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('de-CH') : '—';

export default function ParticipantHistory() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(() => {
    setLoading(true);
    api.get('/admin/participants', { params: { search: debouncedSearch, page } })
      .then(r => { setData(r.data.participants); setTotal(r.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [debouncedSearch, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.ceil(total / 30);

  return (
    <div style={{ display: 'flex' }}>
      <AdminSidebar />
      <main className="main-content">
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 26, marginBottom: 4 }}>Teilnehmer-Verlauf</h1>
          <p style={{ color: 'var(--text3)', fontSize: 14 }}>{total} Teilnehmer insgesamt</p>
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <input
            className="form-input"
            placeholder="Name suchen…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        </div>

        {loading ? (
          <SkeletonCard rows={8} />
        ) : data.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text3)' }}>
            Keine Teilnehmer gefunden.
          </div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
                    {['Name', 'Geburtsdatum', 'Spiele', 'Ø Punkte', 'Bester Score', 'Richtige', 'Letztes Spiel'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 600 }}>
                        {p.first_name} {p.last_name}
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--text3)' }}>
                        {p.birth_date ? fmt(p.birth_date) : '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontWeight: 700, color: 'var(--primary)' }}>{p.games_played}</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>{p.avg_score ?? '—'}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 600 }}>{p.best_score ?? '—'}</td>
                      <td style={{ padding: '12px 16px', color: 'var(--text3)' }}>{p.total_correct ?? '—'}</td>
                      <td style={{ padding: '12px 16px', color: 'var(--text3)', fontSize: 13 }}>{fmt(p.last_played)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, padding: 16, borderTop: '1px solid var(--border)' }}>
                <button className="btn btn-secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  ← Zurück
                </button>
                <span style={{ fontSize: 13, color: 'var(--text3)' }}>Seite {page} von {totalPages}</span>
                <button className="btn btn-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  Weiter →
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
